import { Injectable, inject } from "@angular/core";
import { isNil, sortBy } from "lodash";
import { Observable, Subject, merge } from "rxjs";
import {
    distinctUntilChanged,
    filter,
    map,
    scan,
    share,
    shareReplay,
    switchMap,
    withLatestFrom
} from "rxjs/operators";
import { EntityDTO } from "~app/common/models/entity-models";
import { CreateAtgardLeveransvirkesVolymCommand } from "../commands/atgard-leveransvirkesvolym/CreateAtgardLeveransvirkesVolymCommand";
import { RemoveAtgardLeveransvirkesVolymCommand } from "../commands/atgard-leveransvirkesvolym/RemoveAtgardLeveransvirkesVolymCommand";
import { UpdateAtgardLeveransvirkesVolymCommand } from "../commands/atgard-leveransvirkesvolym/UpdateAtgardLeveransvirkesVolymCommand";
import { VKAtgardLeveransvirkesvolymDTO } from "../models/atgard-leveransvirkesvolym-models";
import { Virkeskostnad } from "../models/virkeskostnader/virkeskostnader-models";
import { AvtalService } from "./avtal.service";
import { KontraktCommandService } from "./kontrakt-command.service";
import { KontraktMediatorService } from "./kontrakt-mediator.service";
import { VirkeskostnaderMediatorService } from "./virkeskostnader-mediator.service";

@Injectable()
export class LeveransvirkesvolymMediatorService {
    readonly leveransvirkesvolymerModified$: Observable<void>;
    readonly updateVolymSrc = new Subject<VKAtgardLeveransvirkesvolymDTO>();

    leveransvirkesvolymer$: Observable<VKAtgardLeveransvirkesvolymDTO[]>;
    virkeskostnader$: Observable<Virkeskostnad[]>;

    private readonly avtalService = inject(AvtalService);
    private readonly kontraktCommandService = inject(KontraktCommandService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly virkeskostnaderMediatorService = inject(VirkeskostnaderMediatorService);

    constructor() {
        const kontraktMediatorService = this.kontraktMediatorService;

        this.virkeskostnader$ = this.virkeskostnaderMediatorService.virkeskostnader$.pipe(
            filter(virkeskostnaderEvent => !isNil(virkeskostnaderEvent)),
            map(virkeskostnaderEvent => virkeskostnaderEvent.virkeskostnader.kontraktVirkeskostnader),
            shareReplay(1)
        );

        const virkeskostnadCreated$ = this.virkeskostnaderMediatorService.virkeskostnadCreated$.pipe(
            withLatestFrom(this.kontraktMediatorService.atgarderPreload$),
            map(([
                virkeskostnad,
                atgarder
            ]) => atgarder
                .map(atgard => this.mapVirkeskostnadToVolym(virkeskostnad.virkeskostnad, atgard.guid))
                .map(volym => this.createVolym(volym))),
            share(),
            map(volymer => (current: VKAtgardLeveransvirkesvolymDTO[]) => [
                ...current,
                ...volymer
            ])
        );

        const virkeskostnadRemoved$ = this.virkeskostnaderMediatorService.virkeskostnadDeleted$.pipe(
            withLatestFrom(this.kontraktMediatorService.atgarderPreload$),
            map(([
                virkeskostnad,
                atgarder
            ]) => atgarder
                .map(atgard => this.mapVirkeskostnadToVolym(virkeskostnad.virkeskostnad, atgard.guid))
                .map(volym => this.removeVolym(volym))),
            share(),
            // eslint-disable-next-line sonarjs/no-nested-functions
            map(volymer => (vm: VKAtgardLeveransvirkesvolymDTO[]) => vm.filter(x => volymer.some(y =>
                y.virkeskostnadGuid !== x.virkeskostnadGuid)))
        );

        const reloadVolymer$ = merge(this.kontraktMediatorService.atgarderPreload$, this.kontraktMediatorService.kontrakt$).pipe(
            withLatestFrom(this.kontraktMediatorService.kontrakt$),
            map(([
                _,
                kontrakt
            ]) => kontrakt),
            filter(kontrakt => kontrakt.id > 0),
            distinctUntilChanged((a, b) => a.guid === b.guid),
            switchMap(kontrakt => this.avtalService.getLeveransvirkesvolymerForAtgard(kontrakt.id)),
            shareReplay(1),
            map(volymer => (_: VKAtgardLeveransvirkesvolymDTO[]) => volymer)
        );

        const synchronize$ = merge(reloadVolymer$, kontraktMediatorService.atgardAdded$).pipe(
            withLatestFrom(this.virkeskostnader$, this.kontraktMediatorService.atgarderPreload$),
            map(([
                _,
                virkeskostnader,
                atgarder
            ]) => (vm: VKAtgardLeveransvirkesvolymDTO[]) => this.synchronize(atgarder, virkeskostnader, vm))
        );

        const updateVolym$ = this.updateVolymSrc.pipe(
            map((volym) => this.updateVolym(volym)),
            share(),
            map(volym => (volymer: VKAtgardLeveransvirkesvolymDTO[]) => {
                const index = volymer.findIndex(v => v.virkeskopsatgardLeveransvirkeGuid === volym.virkeskopsatgardLeveransvirkeGuid &&
                    v.virkeskostnadGuid === volym.virkeskostnadGuid);
                return [
                    ...volymer.slice(0, index),
                    volym,
                    ...volymer.slice(index + 1)
                ];
            })
        );

        this.leveransvirkesvolymerModified$ = merge(virkeskostnadCreated$, virkeskostnadRemoved$, synchronize$, reloadVolymer$)
            .pipe(
                map(_ => null),
                shareReplay(1)
            );

        this.leveransvirkesvolymer$ = merge(
            updateVolym$,
            reloadVolymer$,
            virkeskostnadCreated$,
            virkeskostnadRemoved$,
            synchronize$
        ).pipe(
            scan((
                acc: VKAtgardLeveransvirkesvolymDTO[],
                mutatorFn: (volymer: VKAtgardLeveransvirkesvolymDTO[]) => VKAtgardLeveransvirkesvolymDTO[]) => mutatorFn(acc),
            []),
            map(volymer => sortBy(volymer,[
                p => p.handelssortimentsgruppSekvensnummer,
                p => p.handelssortimentId > 0,
                p => p.handelssortimentNamn?.toLowerCase()
            ])),
            shareReplay(1)
        );
    }

    private createVolym(leveransvirkesVolym: VKAtgardLeveransvirkesvolymDTO) {
        const command = new CreateAtgardLeveransvirkesVolymCommand(leveransvirkesVolym, this.kontraktMediatorService.kontrakt?.guid);
        this.kontraktCommandService.update(command);
        return leveransvirkesVolym;
    }

    private mapVirkeskostnadToVolym(virkeskostnad: Virkeskostnad, atgardGuid: string) {
        const volym: VKAtgardLeveransvirkesvolymDTO = {
            id: 0,
            kvantitet: virkeskostnad.kvantitet,
            virkeskopsatgardLeveransvirkeGuid: atgardGuid,
            virkeskostnadGuid: virkeskostnad.guid,
            handelssortimentNamn: virkeskostnad.handelssortimentNamn,
            mattslag: virkeskostnad.mattslag,
            handelssortimentId: virkeskostnad.handelssortimentId,
            handelssortimentsgruppSekvensnummer: virkeskostnad.handelssortimentsgruppSekvensnummer
        };
        return volym;
    }

    private removeVolym(leveransvirkesVolym: VKAtgardLeveransvirkesvolymDTO) {
        const command = new RemoveAtgardLeveransvirkesVolymCommand(leveransvirkesVolym, this.kontraktMediatorService.kontrakt?.guid);
        this.kontraktCommandService.update(command);
        return leveransvirkesVolym;
    }

    private synchronize(atgarder: EntityDTO[], virkeskostnader: Virkeskostnad[], volymer: VKAtgardLeveransvirkesvolymDTO[]) {
        for (const atgard of atgarder) {
            for (const virkeskostnad of virkeskostnader) {
                const hasVolym = volymer.some(vol =>
                    vol.virkeskostnadGuid === virkeskostnad.guid &&
                    vol.virkeskopsatgardLeveransvirkeGuid === atgard.guid);
                if (hasVolym) {
                    continue;
                }
                const created = this.createVolym(this.mapVirkeskostnadToVolym(virkeskostnad, atgard.guid));

                volymer = [
                    ...volymer,
                    created
                ];
            }

            for (const volym of volymer) {
                const hasVirkeskostad = virkeskostnader.some(vk => vk.guid === volym.virkeskostnadGuid);

                if (hasVirkeskostad) {
                    continue;
                }

                const removed = this.removeVolym(volym);

                volymer = volymer.filter(x =>
                    removed.virkeskopsatgardLeveransvirkeGuid !== x.virkeskopsatgardLeveransvirkeGuid &&
                    removed.virkeskostnadGuid !== x.virkeskostnadGuid);
            }
        }
        return volymer;
    }

    private updateVolym(leveransvirkesVolym: VKAtgardLeveransvirkesvolymDTO) {
        const command = new UpdateAtgardLeveransvirkesVolymCommand(leveransvirkesVolym, this.kontraktMediatorService.kontrakt?.guid);
        this.kontraktCommandService.update(command);
        return leveransvirkesVolym;
    }
}
